import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { 
  Bell, Clock, Calendar, Send, Users, CheckCircle, 
  AlertCircle, Loader2, Settings
} from "lucide-react";
import { format, addHours } from "date-fns";

interface EmailSettings {
  id: string;
  appointment_reminder_enabled: boolean;
  appointment_reminder_hours: number;
  reengagement_enabled: boolean;
  reengagement_days_90: boolean;
  reengagement_days_120: boolean;
  reengagement_days_150: boolean;
}

interface UpcomingAppointment {
  id: string;
  customer_name: string;
  customer_email: string;
  next_appointment_date: string;
  member_number: string;
  locations?: { name: string } | null;
}

export const AutomatedReminders = () => {
  const [sendingReminders, setSendingReminders] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: settings, isLoading: loadingSettings } = useQuery({
    queryKey: ['email-settings-reminders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_settings')
        .select('*')
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data as EmailSettings | null;
    },
  });

  const { data: upcomingAppointments = [] } = useQuery({
    queryKey: ['upcoming-appointments'],
    queryFn: async () => {
      const now = new Date();
      const in72Hours = addHours(now, 72);
      
      const { data, error } = await supabase
        .from('memberships')
        .select(`
          id, customer_name, customer_email, next_appointment_date, member_number,
          locations:location_id (name)
        `)
        .gte('next_appointment_date', now.toISOString())
        .lte('next_appointment_date', in72Hours.toISOString())
        .eq('status', 'active')
        .order('next_appointment_date', { ascending: true });
      
      if (error) throw error;
      return data as UpcomingAppointment[];
    },
  });

  const { data: reengagementCandidates = [] } = useQuery({
    queryKey: ['reengagement-candidates'],
    queryFn: async () => {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      
      const { data, error } = await supabase
        .from('memberships')
        .select('id, customer_name, customer_email, last_visit_at')
        .lt('last_visit_at', ninetyDaysAgo.toISOString())
        .eq('status', 'active')
        .not('last_visit_at', 'is', null);
      
      if (error) throw error;
      return data || [];
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (updates: Partial<EmailSettings>) => {
      if (!settings?.id) {
        // Create new settings if none exist
        const { error } = await supabase
          .from('email_settings')
          .insert({ ...updates });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('email_settings')
          .update(updates)
          .eq('id', settings.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-settings-reminders'] });
      toast({ title: "Settings updated" });
    },
    onError: () => {
      toast({ title: "Failed to update settings", variant: "destructive" });
    },
  });

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-appointment-reminder');
      
      if (error) throw error;
      
      toast({ 
        title: "Reminders Sent", 
        description: `${data.results?.sent || 0} reminders sent successfully` 
      });
    } catch (error) {
      toast({ 
        title: "Error", 
        description: "Failed to send reminders", 
        variant: "destructive" 
      });
    } finally {
      setSendingReminders(false);
    }
  };

  const handleSendReengagement = async () => {
    setSendingReminders(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-reengagement-emails');
      
      if (error) throw error;
      
      toast({ 
        title: "Re-engagement Emails Sent", 
        description: `${data.results?.sent || 0} emails sent` 
      });
    } catch (error) {
      toast({ 
        title: "Error", 
        description: "Failed to send re-engagement emails", 
        variant: "destructive" 
      });
    } finally {
      setSendingReminders(false);
    }
  };

  if (loadingSettings) {
    return <div className="text-center py-8 text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Appointment Reminders
            </CardTitle>
            <CardDescription>
              {upcomingAppointments.length} appointments in next 72 hours
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings?.appointment_reminder_enabled ?? true}
                  onCheckedChange={(checked) => 
                    updateSettingsMutation.mutate({ appointment_reminder_enabled: checked })
                  }
                />
                <Label>Auto-send reminders</Label>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  className="w-16"
                  value={settings?.appointment_reminder_hours ?? 24}
                  onChange={(e) => 
                    updateSettingsMutation.mutate({ 
                      appointment_reminder_hours: parseInt(e.target.value) || 24 
                    })
                  }
                />
                <span className="text-sm text-muted-foreground">hrs before</span>
              </div>
            </div>
            <Button 
              onClick={handleSendReminders} 
              disabled={sendingReminders || upcomingAppointments.length === 0}
              className="w-full"
            >
              {sendingReminders ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Reminders Now
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Re-engagement Campaigns
            </CardTitle>
            <CardDescription>
              {reengagementCandidates.length} members inactive 90+ days
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings?.reengagement_enabled ?? false}
                  onCheckedChange={(checked) => 
                    updateSettingsMutation.mutate({ reengagement_enabled: checked })
                  }
                />
                <Label>Auto-send re-engagement</Label>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Badge 
                variant={settings?.reengagement_days_90 ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => updateSettingsMutation.mutate({ 
                  reengagement_days_90: !settings?.reengagement_days_90 
                })}
              >
                90 days
              </Badge>
              <Badge 
                variant={settings?.reengagement_days_120 ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => updateSettingsMutation.mutate({ 
                  reengagement_days_120: !settings?.reengagement_days_120 
                })}
              >
                120 days
              </Badge>
              <Badge 
                variant={settings?.reengagement_days_150 ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => updateSettingsMutation.mutate({ 
                  reengagement_days_150: !settings?.reengagement_days_150 
                })}
              >
                150 days
              </Badge>
            </div>
            <Button 
              onClick={handleSendReengagement} 
              disabled={sendingReminders || reengagementCandidates.length === 0}
              variant="outline"
              className="w-full"
            >
              {sendingReminders ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Re-engagement Now
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Appointments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Upcoming Appointments (72h)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingAppointments.length === 0 ? (
            <p className="text-center py-4 text-muted-foreground">
              No appointments scheduled in the next 72 hours
            </p>
          ) : (
            <div className="space-y-3">
              {upcomingAppointments.slice(0, 10).map((apt) => (
                <div 
                  key={apt.id} 
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{apt.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{apt.customer_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">
                      {format(new Date(apt.next_appointment_date), 'MMM d, h:mm a')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {apt.locations?.name || 'Location TBD'}
                    </p>
                  </div>
                </div>
              ))}
              {upcomingAppointments.length > 10 && (
                <p className="text-center text-sm text-muted-foreground">
                  +{upcomingAppointments.length - 10} more appointments
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{upcomingAppointments.length}</p>
            <p className="text-sm text-muted-foreground">Pending Reminders</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{reengagementCandidates.length}</p>
            <p className="text-sm text-muted-foreground">Need Re-engagement</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Settings className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">
              {settings?.appointment_reminder_hours || 24}h
            </p>
            <p className="text-sm text-muted-foreground">Reminder Window</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Bell className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <Badge variant={settings?.appointment_reminder_enabled ? "default" : "secondary"}>
              {settings?.appointment_reminder_enabled ? "Active" : "Paused"}
            </Badge>
            <p className="text-sm text-muted-foreground mt-1">Auto-Reminders</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
